import type { ListingPicturesVisibilityMode, REST_schema } from "../../@types/sharedSchema";

/**
 * Two rules about a buyer's minisite page that the dashboard and the server must apply the same way —
 * which page is "the" page, and what a page carries for a listing's presentation. Pure, so they are
 * tested beside their server twins' measurements (minisitePage.test.ts).
 */

/** The listing-intrinsic presentation a page carries for one listing: which photos a buyer sees, and its text. */
export type ListingPresentationEdits = {
  picturesVisibilityMode: ListingPicturesVisibilityMode;
  /** ORIGINAL picture urls. Kept whatever the mode: "show all" and "hide all" park the set, never clear it. */
  hiddenPictures: string[];
  /** "" when the buyer reads the ad's own text. */
  descriptionOverride: string;
};

/**
 * Which of a buyer's pages is THE page, when the realtor holds several for one buyer (nothing enforces
 * one: measured 2026-10-29, 3,261 of 29,551 (realtor, buyer) pairs, nearly all of them empty extras).
 * The first by id that holds listings, else the first by id — the server's `pickMinisite`, which the
 * listing share, the campaigns' minisite links, the office view and Daniela use. So the page the realtor
 * sees and adds to is the page the buyer was sent. `docs[0]` (the first by id) disagreed in the 10 pairs
 * whose first page was empty while a later one held the listings: the realtor saw an empty page, and an
 * add filled the empty one while the buyer kept the other link.
 */
export function pickMinisitePage<D extends { id: string; data(): { listingIds?: unknown } | undefined }>(
  pages: readonly D[]
): D | undefined {
  const sorted = [...pages].sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
  const holdsListings = (page: D): boolean => {
    const listingIds = page.data()?.listingIds;
    return Array.isArray(listingIds) && listingIds.length > 0;
  };
  return sorted.find(holdsListings) ?? sorted[0];
}

/**
 * The realtor's saved presentation of a listing (`GET /listing-presentations/:id`) as what an ADD carries
 * onto a page — or null when the listing is shown as the ad is, so the add writes nothing but the listing.
 */
export function presentationEditsForAdd(
  presentation: Pick<REST_schema.GetListingPresentationResponse, "descriptionOverride" | "picturesVisibilityMode" | "hiddenPictures">
): ListingPresentationEdits | null {
  const hiddenPictures = presentation.hiddenPictures ?? [];
  const customized =
    Boolean(presentation.descriptionOverride) || presentation.picturesVisibilityMode !== "all" || hiddenPictures.length > 0;
  return customized
    ? {
        picturesVisibilityMode: presentation.picturesVisibilityMode,
        hiddenPictures,
        descriptionOverride: presentation.descriptionOverride ?? ""
      }
    : null;
}

/**
 * The fields ONE page carries for a listing's presentation — exactly what the server writes when the
 * realtor saves it (`minisitePresentationFields` in its listingPresentation.service), so a page the
 * listing is ADDED to cannot be told apart from a page the save reached. Field paths, never a whole
 * `additionalDetails` object: that would clobber every other listing's entry and the per-buyer note.
 *
 * The hidden set is written whatever the mode, parked under "show all" / "hide all": the edit dialog
 * reads it back from the page, and a page without it offered the realtor an empty set to save over
 * the one their other buyers' pages hold. `listingIdsWithPicturesHidden` is left to the caller (one
 * transform per field per write, and an add of several listings can hide all of one while showing another).
 */
export function minisitePresentationFields(
  listingId: string,
  edits: ListingPresentationEdits
): { fields: Record<string, unknown>; hidesAllPictures: boolean } {
  return {
    fields: {
      [`additionalDetails.${listingId}.descriptionOverride`]: edits.descriptionOverride?.trim() ?? "",
      [`additionalDetails.${listingId}.hiddenPictures.enabled`]: edits.picturesVisibilityMode === "some",
      [`additionalDetails.${listingId}.hiddenPictures.pictures`]: edits.hiddenPictures ?? []
    },
    hidesAllPictures: edits.picturesVisibilityMode === "none"
  };
}
